
import axios from 'axios';
import {
  defineComponent, onMounted, ref,
} from 'vue';
import { message } from 'ant-design-vue';
import { userClient } from '@/apis';
import log from '@/utils/log';
import { semesterArray } from '@/utils/config';

import {
  TimetableDay,
  TimetableDetailDialogContent,
  TimetableConflictDialogContent,
  TimetableSearchBar,
  TimetableHeadBar,
  TimetableSelectedCourseList,
} from './components';
import {
  RawCourse,
  AllCourses,
} from './types';
import {
  useVuex,
  useSemester,
  useCourseSelection,
  useSearchIndex,
  useCloudSync,
} from './composables';

export default defineComponent({
  components: {
    TimetableDay,
    TimetableDetailDialogContent,
    TimetableConflictDialogContent,
    TimetableSearchBar,
    TimetableHeadBar,
    TimetableSelectedCourseList,
  },
  setup() {
    // TODO: 重命名 course 为 lesson

    /** 在与后端交互失败后进入离线模式，在下一次进入页面时再尝试修正 */
    const isOffline = ref(false);

    const {
      store, isUserLoggedIn, isDetailDialogVisible, hideDetailDialog,
    } = useVuex();

    const { semester, semesterIndex, semesterName } = useSemester();

    const {
      allCourses,
      selectedCoursesIds,
      selectedSectionsByDay,
      initSelectedSectionsByDay,
      isLoadingCourses,
      isSelectedCourseListVisible,
      selectedCourses,
      addSelectedCourse,
      removeSelectedCourse,
      replaceSelectedCourses,
    } = useCourseSelection(semester, isOffline);

    const { isSearchDialogVisible, initSearchIndex, searchIndex } = useSearchIndex(allCourses);

    const {
      fetchSelectedCourses,
      selectedCoursesIdsFromDatabase,
      isConflictDialogVisible,
      onConflictResolved,
    } = useCloudSync(semester, selectedCoursesIds, replaceSelectedCourses, isOffline);

    // 课程数据读取
    /** 从 JSON 中读取课程数据 */
    const getCoursesFromJSON = (JSONFileName: string) => {
      const filePath = `lessons/${JSONFileName}`;
      isLoadingCourses.value = true;
      axios
        .get(filePath)
        .then((resp) => {
          const allCoursesCache = {} as AllCourses;
          resp.data.forEach((course: RawCourse) => {
            if (course && course.id) {
              allCoursesCache[course.id] = course;
            }
          });
          allCourses.value = allCoursesCache;

          // 初始化
          initSelectedSectionsByDay();
          initSearchIndex();
          isLoadingCourses.value = false;

          // 若用户已登录，从后端同步所选课程 Id 列表
          if (isUserLoggedIn.value && !store.state.hasFetchedSelectedCourses) {
            fetchSelectedCourses();
            // 顺便更新用户信息
            userClient
              .getUserInfo({})
              .then((profile) => {
                store.commit('setUserProfile', profile);
              })
              .catch((err) => {
                log.error(err);
              });
          }
        })
        .catch((err) => {
          message.error('拉取课程数据失败，请尝试刷新页面', 1.5);
          throw err;
        });
    };
    onMounted(() => {
      // 读取课程信息
      getCoursesFromJSON(semesterArray[semesterIndex.value].jsonFileName);
    });

    /** 变更学期 */
    const moveSemester = (step: -1 | 1) => {
      if (step === -1 && semesterIndex.value === 0) {
        message.warn('已经是最后一个学期啦', 1);
        return;
      }
      if (step === 1 && semesterIndex.value === semesterArray.length - 1) {
        message.warn('已经是最新学期啦', 1);
        return;
      }
      semester.value = semesterArray[semesterIndex.value + step].key;
      selectedCoursesIds.value = new Set(store.state.selectedCoursesIds[semester.value]);
      selectedSectionsByDay.value = [{}, {}, {}, {}, {}, {}, {}];
      getCoursesFromJSON(semesterArray[semesterIndex.value].jsonFileName);
      fetchSelectedCourses(true);

      store.commit('setSemester', semester.value);
    };

    /** 根据 lesson id 显示详情页 */
    const handleShowDetail = (courseId: number) => {
      isSelectedCourseListVisible.value = false;
      store.commit('changeDetailPageContent', allCourses.value[courseId]);
      store.commit('showDetailDialog');
    };

    return {
      isUserLoggedIn,
      isDetailDialogVisible,
      hideDetailDialog,
      isSelectedCourseListVisible,
      selectedCoursesIds,
      selectedSectionsByDay,
      selectedCourses,
      addSelectedCourse,
      removeSelectedCourse,
      isSearchDialogVisible,
      searchIndex,
      selectedCoursesIdsFromDatabase,
      isConflictDialogVisible,
      onConflictResolved,
      fetchSelectedCourses,
      allCourses,
      isLoadingCourses,
      semester,
      semesterIndex,
      semesterName,
      semesterArray,
      moveSemester,
      handleShowDetail,
    };
  },
});
